// ─────────────────────────────────────────────────────────────────────────────
// useEventPerformance
//
// Behavioral analytics: how does the user trade around high-impact economic
// events? Compares trades on event days vs non-event days, and breaks
// performance down by event class (FOMC, CPI, NFP, etc.).
//
// Inputs:
//   - trades: array of trades from useSupabase
//   - accountId: filter to this account ('' or null = all accounts)
//   - daysBack: how far back to look (90, 180, 365)
//
// Outputs:
//   - loading: boolean (events still fetching from cache)
//   - error: string | null
//   - hasData: boolean (any trades in the window?)
//   - hasEventDayTrades: boolean (any of those trades fell on event days?)
//   - headline: { eventDay: {...stats}, nonEventDay: {...stats} }
//   - byClass: array of { class, stats } sorted by sample size desc
//
// Stats shape:
//   { trades: number, wins: number, losses: number, winRate: number|null,
//     totalPnl: number, avgPnl: number|null }
//
// Sample-size policy:
//   - Buckets with <3 trades are excluded from byClass (too noisy)
//   - Buckets with 3-9 trades are flagged via `lowSample: true` so the UI
//     can render uncertainty language
//   - Buckets with 10+ trades are presented straight
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import { SUPABASE_URL, authFetch } from "../lib/supabase";

const LOW_SAMPLE_THRESHOLD = 3;   // below this, hide entirely
const CONFIDENT_THRESHOLD  = 10;  // at/above this, no uncertainty caveats

export function useEventPerformance(trades, accountId, daysBack = 180) {
  // ── 1. Fetch high-impact US events from cache ─────────────────────────────
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const cutoff = new Date(Date.now() - daysBack * 86400000)
      .toISOString().split("T")[0];

    // Only high-impact events drive the headline. We could expand to medium
    // later, but high-impact is the signal users actually care about.
    const url = `${SUPABASE_URL}/rest/v1/economic_events_cache` +
      `?select=event_date,event_time,event_name,event_class,impact` +
      `&impact=eq.high` +
      `&event_date=gte.${cutoff}` +
      `&order=event_date.asc`;

    authFetch(url)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => { if (!cancelled) { setEvents(data); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });

    return () => { cancelled = true; };
  }, [daysBack]);

  // ── 2. Compute analytics ──────────────────────────────────────────────────
  return useMemo(() => {
    if (loading || error || !events) {
      return {
        loading,
        error,
        hasData: false,
        hasEventDayTrades: false,
        headline: null,
        byClass: [],
      };
    }

    // Filter trades to window + account
    const cutoff = new Date(Date.now() - daysBack * 86400000)
      .toISOString().split("T")[0];

    const relevantTrades = (trades || []).filter(t => {
      if (!t.trade_date) return false;
      if (t.trade_date < cutoff) return false;
      if (accountId && t.account_id !== accountId) return false;
      // Only finished trades — open positions don't have a meaningful pnl yet.
      if (t.pnl === null || t.pnl === undefined) return false;
      return true;
    });

    if (relevantTrades.length === 0) {
      return {
        loading: false,
        error: null,
        hasData: false,
        hasEventDayTrades: false,
        headline: null,
        byClass: [],
      };
    }

    // Build a Set of event dates (YYYY-MM-DD) for O(1) day-based matching
    const eventDays = new Set(events.map(e => e.event_date));

    // Map each event date to its classes (for the class breakdown)
    // A single day can have multiple high-impact events of different classes
    // (e.g. CPI + Retail Sales on the same day). We track them separately so
    // a trade on that day appears in BOTH buckets.
    const eventDateToClasses = new Map();
    for (const e of events) {
      if (!e.event_class) continue;  // skip unclassified events for byClass view
      if (!eventDateToClasses.has(e.event_date)) {
        eventDateToClasses.set(e.event_date, new Set());
      }
      eventDateToClasses.get(e.event_date).add(e.event_class);
    }

    // ── Headline: event-day vs non-event-day ────────────────────────────────
    const eventDayTrades = [];
    const nonEventDayTrades = [];

    for (const t of relevantTrades) {
      const dateOnly = String(t.trade_date).slice(0, 10);
      if (eventDays.has(dateOnly)) {
        eventDayTrades.push(t);
      } else {
        nonEventDayTrades.push(t);
      }
    }

    const headline = {
      eventDay:    summarize(eventDayTrades),
      nonEventDay: summarize(nonEventDayTrades),
    };

    // ── By class ─────────────────────────────────────────────────────────────
    // Group trades by event class. A trade on a date with two event classes
    // (e.g. CPI + Retail Sales) gets counted in both buckets — this is
    // intentional: the user is reporting on BOTH events that day.
    const tradesByClass = {};
    for (const t of relevantTrades) {
      const dateOnly = String(t.trade_date).slice(0, 10);
      const classes = eventDateToClasses.get(dateOnly);
      if (!classes) continue;
      for (const cls of classes) {
        if (!tradesByClass[cls]) tradesByClass[cls] = [];
        tradesByClass[cls].push(t);
      }
    }

    const byClass = Object.entries(tradesByClass)
      .map(([cls, ts]) => ({
        class: cls,
        ...summarize(ts),
        lowSample: ts.length < CONFIDENT_THRESHOLD,
      }))
      .filter(b => b.trades >= LOW_SAMPLE_THRESHOLD)
      .sort((a, b) => b.trades - a.trades);

    return {
      loading: false,
      error: null,
      hasData: true,
      hasEventDayTrades: eventDayTrades.length > 0,
      headline,
      byClass,
    };
  }, [events, trades, accountId, daysBack, loading, error]);
}

// ── Pure summarization helper ──────────────────────────────────────────────
//
// Wins are defined as net P&L > 0 (after fees and commissions). This matches
// how the Dashboard computes "Today's Performance" and other stats — keeping
// the definition consistent prevents the embarrassing "Dashboard says 50%
// win rate, this panel says 47%" inconsistency.

function summarize(trades) {
  if (trades.length === 0) {
    return { trades: 0, wins: 0, losses: 0, winRate: null, totalPnl: 0, avgPnl: null };
  }
  let wins = 0;
  let totalPnl = 0;
  for (const t of trades) {
    const net = (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0);
    totalPnl += net;
    if (net > 0) wins++;
  }
  const losses = trades.length - wins;
  return {
    trades: trades.length,
    wins,
    losses,
    winRate: wins / trades.length,
    totalPnl,
    avgPnl: totalPnl / trades.length,
  };
}
